import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  makeSyncReduxWithLocalStorageObserver,
  restoreFromLocalStorage,
} from 'app/dataManagement/syncReduxStateWithLocalStorage';
import { WMConfiguration } from 'app/dicom/WorklistMonitor';
import { AssociationParameters } from 'app/interfaces/PACSCommunication';
import observeStore from 'app/utils/redux/observeStore';
import _ from 'lodash';
import { Store } from 'redux';

export const PACS_CONFIGURATION_LOCAL_STORAGE_KEY = 'redux.PACSConfiguration';

export type PACSAutomaticUpload = {
  enabled?: boolean;
  fetchInterval?: number;
};

export type PACSConfigurationState = AssociationParameters & { enabled?: boolean } & {
  worklist?: WMConfiguration;
  automaticUpload?: PACSAutomaticUpload;
};

const pacsConfigurationSlice = createSlice({
  name: 'PACSConfiguration',
  initialState: () =>
    Object.freeze<PACSConfigurationState>(
      restoreFromLocalStorage(PACS_CONFIGURATION_LOCAL_STORAGE_KEY, {})
    ),
  reducers: {
    setPACSEnabled(state: PACSConfigurationState, action: PayloadAction<boolean>) {
      state.enabled = action.payload;
    },
    setPACSConfiguration(
      state: PACSConfigurationState,
      action: PayloadAction<PACSConfigurationState>
    ) {
      return _.cloneDeep(action.payload);
    },
    updatePACSAssociation(
      state: PACSConfigurationState,
      action: PayloadAction<AssociationParameters & { enabled?: boolean }>
    ) {
      _.merge(state, _.cloneDeep(action.payload));
    },
    updatePACSWorklist(
      state: PACSConfigurationState,
      action: PayloadAction<Partial<WMConfiguration>>
    ) {
      state.worklist = _.merge({}, state.worklist, _.cloneDeep(action.payload));
    },
  },
});

type StoreWithPACSConfigurationState = { PACSConfiguration: PACSConfigurationState };

export const selectPACSConfiguration = (
  state: StoreWithPACSConfigurationState
): PACSConfigurationState => state.PACSConfiguration;

export const isPACSConfigurationDisabled = (PACSConfiguration: PACSConfigurationState) => {
  return !PACSConfiguration?.enabled;
};

export const attachPACSConfigurationWithLocalStorageSynchronizationObserver = <
  S extends StoreWithPACSConfigurationState
>(
  store: Store<S>
) => {
  observeStore(
    store,
    selectPACSConfiguration,
    makeSyncReduxWithLocalStorageObserver(PACS_CONFIGURATION_LOCAL_STORAGE_KEY)
  );
};

const PACSConfigurationReducer = pacsConfigurationSlice.reducer;
const { setPACSEnabled, setPACSConfiguration, updatePACSAssociation, updatePACSWorklist } =
  pacsConfigurationSlice.actions;

export { setPACSEnabled, setPACSConfiguration, updatePACSAssociation, updatePACSWorklist };
export default PACSConfigurationReducer;
